import React, { useEffect, useMemo, useState } from 'react';
import { Save, Check, Trash2 } from 'lucide-react';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import FormControl from '../../common/FormControl';
import { AggregationDefinition, AggregationFunction, AggregationType, ColumnDataType, DataSource } from '../types';
import { colDataTypeToLegacy } from '../operators';
import FileAnalyticsAPI from '../fileAnalyticsService';

interface AggregationBuilderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
  /** When editing, a Delete button appears in the footer and calls this. */
  onDelete?: () => void;
  collectionTypeId?: string;
  /** When set, the modal edits this aggregation instead of creating a new one. */
  aggregation?: AggregationDefinition | null;
  /**
   * When launched from the data-source panel, pre-scope the aggregation to this
   * data source and lock the source picker so the user can't switch it.
   */
  initialDataSourceId?: string;
}

const TYPE_LABELS: Record<AggregationType, string> = {
  group_by: 'Group By (single column)',
  multi_group_by: 'Multi Group By',
  pivot: 'Pivot (rows × columns × measure)',
  sumif: 'SUMIF (filter + sum)',
  moving_average: 'Moving Average',
  part_to_whole: 'Part-to-Whole (share of total)',
};

// Litigation-style group box: legend-on-the-border fieldset, no hint subtext.
const StepGroup: React.FC<{ title: string; id?: string; children: React.ReactNode }> = ({ title, id, children }) => (
  <fieldset data-step={id} className="field-group">
    <legend className="field-group-legend">{title}</legend>
    <div className="field-group-body">{children}</div>
  </fieldset>
);

const AGG_FUNCS: { value: AggregationFunction; label: string }[] = [
  { value: 'sum',            label: 'SUM' },
  { value: 'avg',            label: 'AVG' },
  { value: 'count',          label: 'COUNT' },
  { value: 'distinct_count', label: 'COUNT DISTINCT' },
  { value: 'min',            label: 'MIN' },
  { value: 'max',            label: 'MAX' },
];

// A resolved column option: name + semantic type. Mirrors RuleBuilderModal's ColOpt.
type ColOpt = { name: string; type: ColumnDataType };

const MEASURE_TYPES: ColumnDataType[] = ['numeric', 'continuous'];
const GROUPABLE_TYPES: ColumnDataType[] = ['categorical', 'date', 'text', 'numeric'];


// ── Main component ─────────────────────────────────────────────────────────

const AggregationBuilderModal: React.FC<AggregationBuilderModalProps> = ({ isOpen, onClose, onSaved, onDelete, collectionTypeId, aggregation = null, initialDataSourceId }) => {
  const [name, setName] = useState('');
  const [type, setType] = useState<AggregationType>('group_by');
  // Pipeline mode: the aggregation reads ONE of the pipeline's linked data sources.
  const [linkedSources, setLinkedSources] = useState<DataSource[]>([]);
  // Single data source id (the one source this aggregation reads).
  const [dataSourceId, setDataSourceId] = useState('');
  // Enriched columns derived from the bound source's Enrich/Combine links (name → type).
  const [enrichedCols, setEnrichedCols] = useState<ColOpt[]>([]);
  const [groupCols, setGroupCols] = useState<string[]>([]);
  const [pivotCol, setPivotCol] = useState('');
  const [measureCol, setMeasureCol] = useState('');
  const [aggs, setAggs] = useState<AggregationFunction[]>(['sum']);
  const [windowSize, setWindowSize] = useState(7);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // ── Open / seed effect ────────────────────────────────────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const editing = !!aggregation;
    setError(null);
    setName(editing ? aggregation!.name : '');
    setType(editing ? aggregation!.type : 'group_by');
    setGroupCols(editing ? (aggregation!.group_by_columns ?? []) : []);
    setPivotCol(editing ? (aggregation!.pivot_column ?? '') : '');
    setMeasureCol(editing ? (aggregation!.measure_column ?? '') : '');
    setAggs(editing
      ? (aggregation!.measure_aggregations?.length
          ? aggregation!.measure_aggregations
          : aggregation!.measure_aggregation ? [aggregation!.measure_aggregation] : ['sum'])
      : ['sum']);
    setWindowSize(editing ? (aggregation!.window_size ?? 7) : 7);
    setFilter(editing ? (aggregation!.filter_expression ?? '') : '');
    setEnrichedCols([]);
    (async () => {
      try {
        // Load data sources: scoped to pipeline when collectionTypeId set,
        // or just the single pre-set source when initialDataSourceId set,
        // otherwise all entity sources.
        const links: DataSource[] = collectionTypeId
          ? await FileAnalyticsAPI.getPipelineDataSources(collectionTypeId)
          : await FileAnalyticsAPI.listSources();
        setLinkedSources(links);

        // Seed the bound data source id — prefer initialDataSourceId (data-source panel context).
        const initialDs = editing
          ? (aggregation!.data_source_id != null
              ? String(aggregation!.data_source_id)
              : (initialDataSourceId ?? (links[0] ? String(links[0].id) : '')))
          : (initialDataSourceId ?? (links[0] ? String(links[0].id) : ''));
        setDataSourceId(initialDs);
        if (initialDs) await loadEnriched(initialDs, links);
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load data sources');
      }
    })();
  }, [isOpen, aggregation, initialDataSourceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Enriched-column loader (Enrich selected_columns + Combine ref schema) ──
  //
  // The aggregation may reference the source's ENRICHED columns, not only its
  // base standard_schema. Enrich links contribute their selected_columns; Combine
  // links contribute the ref source's full schema. We default enriched columns to
  // a permissive type so they appear in BOTH group-by and measure lists unless we
  // can infer a stricter type from the ref source's schema.
  const loadEnriched = async (dsId: string, sources: DataSource[]) => {
    try {
      const links = await FileAnalyticsAPI.listSourceLinks(Number(dsId));
      // Enrich/Combine refs are standalone sources, NOT necessarily pipeline-linked,
      // so resolve their schemas from ALL entity sources — otherwise an enriched
      // numeric column (e.g. invoice_amount) defaults to 'text' and never appears
      // as a measure.
      const allSources = await FileAnalyticsAPI.listSources().catch(() => [] as DataSource[]);
      const byId = new Map([...sources, ...allSources].map(s => [String(s.id), s]));
      const out: ColOpt[] = [];
      const seen = new Set<string>();
      for (const link of links) {
        const ref = byId.get(String(link.ref_source_id));
        const refSchema = ref?.standard_schema ?? [];
        const refType = (n: string): ColumnDataType =>
          colDataTypeToLegacy((refSchema.find(c => c.name === n) as any)?.data_type ?? (refSchema.find(c => c.name === n) as any)?.semantic_type ?? 'text');
        if (link.role === 'enrich') {
          for (const n of (link.selected_columns ?? [])) {
            if (!seen.has(n)) { seen.add(n); out.push({ name: n, type: refType(n) }); }
          }
        } else if (link.role === 'combine') {
          for (const c of refSchema) {
            const ct = colDataTypeToLegacy((c as any).data_type ?? (c as any).semantic_type ?? 'text');
            if (!seen.has(c.name)) { seen.add(c.name); out.push({ name: c.name, type: ct }); }
          }
        }
      }
      setEnrichedCols(out);
    } catch {
      // Links are optional — a source with no links just has no enriched columns.
      setEnrichedCols([]);
    }
  };

  // ── Switching the data source reloads its enriched columns and clears choices ─

  const onDataSourceChange = async (dsId: string) => {
    setDataSourceId(dsId);
    setGroupCols([]); setMeasureCol(''); setPivotCol('');
    setEnrichedCols([]);
    if (dsId) await loadEnriched(dsId, linkedSources);
  };

  // ── Column derivation ─────────────────────────────────────────────────────
  //
  // Columns come from the BOUND data source's schema (+ its enriched columns),
  // NOT from a sample dataset. Mirror RuleBuilderModal.pipelineColOptions.

  const allColOptions = useMemo<ColOpt[]>(() => {
    const ds = linkedSources.find(l => String(l.id) === dataSourceId);
    const base: ColOpt[] = (ds?.standard_schema ?? []).map(c => ({ name: c.name, type: colDataTypeToLegacy((c as any).data_type ?? (c as any).semantic_type ?? 'text') }));
    // Dedup by name; base schema wins (it carries the authoritative semantic type).
    const seen = new Set(base.map(c => c.name));
    const merged = [...base];
    for (const c of enrichedCols) if (!seen.has(c.name)) { seen.add(c.name); merged.push(c); }
    return merged;
  }, [linkedSources, dataSourceId, enrichedCols]);

  const numericCols = allColOptions.filter(c => MEASURE_TYPES.includes(c.type));
  const groupableCols = allColOptions.filter(c => GROUPABLE_TYPES.includes(c.type));

  // ── Submit ────────────────────────────────────────────────────────────────
  //
  // The backend requires source_dataset_id (fan_aggregation.source_dataset_id is
  // NOT NULL). The user no longer picks a sample dataset; instead we resolve it
  // automatically from the bound data source's latest Ready dataset.

  const resolveSourceDatasetId = async (dsId: string): Promise<string | null> => {
    if (!dsId) return null;
    try {
      const srcRows = await FileAnalyticsAPI.listSources(dsId);
      const all = [] as Awaited<ReturnType<typeof FileAnalyticsAPI.listDatasets>>;
      for (const src of srcRows) {
        const ds = await FileAnalyticsAPI.listDatasets({ source_id: src.id });
        all.push(...ds);
      }
      const ready = all.filter(d => d.status === 'Ready');
      if (!ready.length) return null;
      // Most-recent Ready dataset by upload time (fallback to last in list order).
      ready.sort((a, b) => (b.uploaded_at || '').localeCompare(a.uploaded_at || ''));
      return ready[0].id;
    } catch {
      return null;
    }
  };

  const submit = async () => {
    if (!name.trim() || !type) {
      setError('Name and type are required');
      return;
    }
    const hasSources = linkedSources.length > 0;
    if (hasSources && !dataSourceId) {
      setError('Choose the data source this aggregation reads');
      return;
    }

    const spec: Record<string, any> = {};
    if (groupCols.length) spec.group_by_columns = groupCols;
    if (measureCol) spec.measure_column = measureCol;
    spec.measure_aggregation = aggs[0];
    if (aggs.length > 1) spec.measure_aggregations = aggs;
    if (pivotCol) spec.pivot_column = pivotCol;
    if (type === 'moving_average') spec.window_size = windowSize;
    if (type === 'sumif' && filter) spec.filter_expression = filter;

    setBusy(true);
    setError(null);
    try {
      // Resolve the backing dataset from the bound source (the user does not pick one).
      const sourceDatasetId = aggregation
        ? String(aggregation.source_dataset_id)
        : await resolveSourceDatasetId(dataSourceId);

      const body = {
        name: name.trim(),
        source_dataset_id: sourceDatasetId || undefined,
        data_source_id: dataSourceId ? Number(dataSourceId) : undefined,
        type,
        spec,
        run_now: !!sourceDatasetId,
      };

      if (aggregation) {
        await FileAnalyticsAPI.updateAggregation(aggregation.id, body);
      } else {
        await FileAnalyticsAPI.createAggregation(body);
      }
      onSaved();
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  // ── Derived UI state ──────────────────────────────────────────────────────

  const isLocked = !!aggregation;
  // Lock the source picker when launched from the data-source panel (initialDataSourceId set).
  const isSourceLocked = isLocked || !!initialDataSourceId;
  const showSourcesSection = linkedSources.length > 0 && !initialDataSourceId;
  const colsReady = !!dataSourceId;

  // Which optional sections are visible for the current summary type.
  const showBreakdown = ['group_by', 'multi_group_by', 'pivot', 'part_to_whole', 'moving_average'].includes(type);
  const showMeasure = ['group_by', 'multi_group_by', 'pivot', 'sumif', 'part_to_whole', 'moving_average'].includes(type);

  return (
    <Modal isOpen={isOpen} onClose={busy ? () => {} : onClose} size="lg">
      <Modal.Header>{aggregation ? 'Edit Aggregation' : 'Create Aggregation'}</Modal.Header>
      <Modal.Body>
        <div className="flex flex-col gap-y-5">

          {/* ── Data source picker — always shown, loads all data sources ── */}
          {showSourcesSection && (
            <StepGroup id="sources" title="Which data source?">
              <FormControl.SearchSelect
                label="Data source"
                value={dataSourceId}
                onChange={val => onDataSourceChange(String(val ?? ''))}
                options={[
                  { value: '', label: '— select —' },
                  ...linkedSources.map(s => ({ value: String(s.id), label: s.name })),
                ]}
                disabled={busy || isSourceLocked}
                data-testid="SEL_fa_agg_data_source"
              />
            </StepGroup>
          )}

          {/* ── 1 — identify ── */}
          <StepGroup id="identify" title="What are you summarising?">
            <FormControl.Input
              label="Name"
              required
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Sales by Store"
              disabled={busy}
            />
          </StepGroup>

          {/* ── 2 — method ── */}
          <StepGroup id="method" title="How should it be summarised?">
            <FormControl.SearchSelect
              label="Summary type"
              value={type}
              onChange={val => setType((val as AggregationType) ?? 'group_by')}
              options={Object.entries(TYPE_LABELS).map(([k, v]) => ({ value: k, label: v }))}
              disabled={busy}
            />
          </StepGroup>

          {/* ── 3 — breakdown ── */}
          {showBreakdown && (
            <StepGroup id="breakdown" title="Group by (one row per these columns)">
              {(() => {
                const opts = groupableCols.map(c => ({ value: c.name, label: `${c.name} (${c.type})` }));
                const placeholder = !colsReady
                  ? 'Select a data source first'
                  : (groupableCols.length === 0 ? 'No groupable columns in this source' : 'Choose columns…');
                return (
                  <FormControl.MultiSelect
                    label="Columns"
                    value={groupCols}
                    onChange={vals => setGroupCols(vals.map(String))}
                    options={opts}
                    placeholder={placeholder}
                    disabled={busy || opts.length === 0}
                  />
                );
              })()}
              {type === 'pivot' && (
                <FormControl.SearchSelect
                  label="Column to spread across the top (pivot)"
                  value={pivotCol}
                  onChange={val => setPivotCol(String(val ?? ''))}
                  options={[{ value: '', label: '— select —' }, ...groupableCols.map(c => ({ value: c.name, label: c.name }))]}
                  disabled={busy}
                />
              )}
            </StepGroup>
          )}

          {/* ── 4 — measure ── */}
          {showMeasure && (
            <StepGroup id="measure" title="What number to compute?">
              <FormControl.SearchSelect
                label="Measure column"
                value={measureCol}
                onChange={val => setMeasureCol(String(val ?? ''))}
                options={[{ value: '', label: '— select —' }, ...numericCols.map(c => ({ value: c.name, label: `${c.name} (${c.type})` }))]}
                disabled={busy || numericCols.length === 0}
                helper={colsReady && numericCols.length === 0 ? 'No numeric columns found — leave blank for a row count.' : undefined}
              />
              {type !== 'moving_average' && (
                <FormControl.MultiSelect
                  label="How to combine it"
                  value={aggs}
                  onChange={vals => { if (vals.length) setAggs(vals.map(String) as AggregationFunction[]); }}
                  options={AGG_FUNCS.map(fn => ({ value: fn.value, label: fn.label }))}
                  placeholder="Choose one or more…"
                  disabled={busy}
                />
              )}
              {type === 'moving_average' && (
                <FormControl.Input
                  label="Window size (rows)"
                  type="number"
                  value={String(windowSize)}
                  onChange={e => setWindowSize(parseInt(e.target.value, 10) || 1)}
                  disabled={busy}
                />
              )}
              {type === 'sumif' && (
                <FormControl.Input
                  label="Only include rows where…"
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  placeholder={`e.g. channel = 'Online' AND amount > 1000`}
                  disabled={busy}
                />
              )}
            </StepGroup>
          )}

          {error && <div className="modal-error-banner">{error}</div>}
        </div>
      </Modal.Body>
      <Modal.Footer>
        {aggregation && onDelete && (
          <Button variant="delete-soft" icon={Trash2} onClick={onDelete} disabled={busy} style={{ marginRight: 'auto' }} data-testid="BTN_fa_agg_delete_modal">
            Delete
          </Button>
        )}
        <Button variant={aggregation ? 'update' : 'fill'} icon={aggregation ? Check : Save} onClick={submit} disabled={busy || !name.trim()} data-testid="BTN_fa_agg_submit">
          {busy ? 'Saving…' : aggregation ? 'Update' : 'Submit'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AggregationBuilderModal;
